import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

const HEADER_PANEL_DESKTOP_MQ = window.matchMedia('(min-width: 750px)');
let documentScrollLockCount = 0;

export function lockDocumentScroll() {
  documentScrollLockCount += 1;
  document.documentElement.setAttribute('dialog-scroll-lock', '');
}

export function unlockDocumentScroll() {
  documentScrollLockCount = Math.max(0, documentScrollLockCount - 1);
  if (documentScrollLockCount === 0) {
    document.documentElement.removeAttribute('dialog-scroll-lock');
  }
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** @type {Element | null} */
  #previouslyFocused = null;

  /** @type {boolean} */
  #usedBodyScrollLock = false;

  /** @type {boolean} */
  #usedDocumentScrollLock = false;

  /** @type {boolean} */
  #usedNonModalHeaderPanel = false;

  /**
   * Scroll position captured when the dialog opened.
   * @returns {number}
   */
  get restoreScrollPosition() {
    return this.#previousScrollY;
  }

  /**
   * Restores the page scroll position saved when the dialog opened.
   * @param {number} [scrollY]
   */
  restorePageScroll(scrollY = this.#previousScrollY) {
    const html = document.documentElement;
    const previousScrollBehavior = html.style.scrollBehavior;

    html.style.scrollBehavior = 'auto';
    html.scrollTop = scrollY;
    window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
    html.style.scrollBehavior = previousScrollBehavior;
  }

  /**
   * Restores scroll after focus changes or history navigation that can jump the page.
   * @param {number} [scrollY]
   */
  scheduleScrollRestore(scrollY = this.#previousScrollY) {
    this.restorePageScroll(scrollY);
    requestAnimationFrame(() => {
      this.restorePageScroll(scrollY);
      requestAnimationFrame(() => this.restorePageScroll(scrollY));
    });
  }

  /**
   * Whether this dialog opens as a full-width header panel on desktop.
   * @returns {boolean}
   */
  get #isHeaderPanelDesktop() {
    return this.hasAttribute('header-panel') && HEADER_PANEL_DESKTOP_MQ.matches;
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#previouslyFocused = document.activeElement;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      this.#usedNonModalHeaderPanel = this.#isHeaderPanelDesktop;
      this.#usedBodyScrollLock = !this.#usedNonModalHeaderPanel && !HEADER_PANEL_DESKTOP_MQ.matches;
      this.#usedDocumentScrollLock = !this.#usedNonModalHeaderPanel && HEADER_PANEL_DESKTOP_MQ.matches;

      if (this.#usedBodyScrollLock) {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
      }

      if (this.#usedDocumentScrollLock) {
        lockDocumentScroll();
      }

      if (this.#usedNonModalHeaderPanel) {
        dialog.show();
        document.addEventListener('click', this.#handleDocumentClick, true);
        document.addEventListener('keydown', this.#handleDocumentKeyDown, true);
      } else {
        dialog.showModal();
      }
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async (options) => {
    const { dialog } = this.refs;
    const restoreFocus = !options || !('restoreFocus' in options) || options.restoreFocus !== false;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    document.removeEventListener('click', this.#handleDocumentClick, true);
    document.removeEventListener('keydown', this.#handleDocumentKeyDown, true);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    const scrollY = this.#previousScrollY;
    const focusTarget = this.#previouslyFocused;

    if (this.#usedBodyScrollLock) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
    }

    if (this.#usedDocumentScrollLock) {
      unlockDocumentScroll();
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    if (restoreFocus && focusTarget instanceof HTMLElement && document.contains(focusTarget)) {
      focusTarget.focus({ preventScroll: true });
    }

    if (this.#usedBodyScrollLock) {
      this.scheduleScrollRestore(scrollY);
    }

    this.#usedBodyScrollLock = false;
    this.#usedDocumentScrollLock = false;
    this.#usedNonModalHeaderPanel = false;

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes non-modal header panels when clicking outside of the dialog.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleDocumentClick = (event) => {
    const { dialog } = this.refs;

    if (!this.#usedNonModalHeaderPanel || !dialog.open) return;
    if (event.target instanceof Node && dialog.contains(event.target)) return;

    if (event.clientY >= dialog.getBoundingClientRect().bottom) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.closeDialog({ restoreFocus: false });
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Closes non-modal header panels when Escape is pressed outside of the dialog.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleDocumentKeyDown = (event) => {
    if (!this.#usedNonModalHeaderPanel || event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
